import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { financeKeys } from "@/modules/finances/gateway/financeKeys"
import { usePeriodParam } from "@/modules/finances/hooks/usePeriod"
import { downloadZipFile } from "@/modules/utils/downloadFile"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"

export const useRefundTransaction = () => {
  const { userFinanceApi, boxFinanceApi } = useServicesContext()
  const { result: boxId } = useCurrentBox()
  const queryClient = useQueryClient()

  const financeApi = boxId ? boxFinanceApi : userFinanceApi
  const apiKey = boxId ? "boxTransactionDetails" : "userTransactionDetails"

  const { mutateAsync: refundTransaction, isPending } = useMutation({
    mutationFn: async (transactionId: string) => {
      await financeApi.refundTransaction(transactionId)
      return transactionId
    },
    onSuccess: async (transactionId: string) => {
      toast.success(word("box.transaction.refund.modal.toast.success"), {
        classNames: successToastClassName(),
      })

      await queryClient.invalidateQueries({
        queryKey: [apiKey, transactionId],
      })

      await new Promise((resolve) => setTimeout(resolve, 1000)) // Delay to allow the backend to update data from Stripe before invalidating queries

      await queryClient.invalidateQueries({
        queryKey: financeKeys.all,
      })
    },
    onError: () => {
      toast.error(word("box.transaction.refund.modal.toast.error"), {
        classNames: errorToastClassName(),
      })
    },
  })

  return {
    refundTransaction,
    isPending,
  }
}

export const useDownloadAllMonthlyInvoices = () => {
  const { boxFinanceApi, userFinanceApi } = useServicesContext()
  const { result: boxId } = useCurrentBox()
  const [period] = usePeriodParam()

  const financeApi = boxId ? boxFinanceApi : userFinanceApi

  const downloadAllMonthlyInvoicesMutation = useMutation({
    mutationFn: async () => {
      const yearMonth = dayjs(period).format("YYYY-MM")
      return await financeApi.exportMonthlyInvoices(yearMonth)
    },
    onSuccess: (result) => {
      downloadZipFile(result.filename ?? `Billing_${dayjs(period).format("MMMM_YYYY")}.zip`, result.blob)
      toast.success(word("finances.page.export.success"), { classNames: successToastClassName() })
    },
    onError: () => {
      toast.error(word("global.error.label"), { classNames: errorToastClassName() })
    },
  })

  return {
    downloadAllMonthlyInvoicesMutation,
  }
}
